import { Checkout } from '@df/checkout/checkout.interfaces';

declare global {
    interface Window {
        Cardinal?: NBarclaycardCybersource.ICardinalInstance;
        Flex: new (token: string) => NBarclaycardCybersource.IFlexInstance;
    }
}

export namespace NBarclaycardCybersource {
    export const AUTH_ERROR_MESSAGE = 'Authentication failed';

    export enum EEndpoints {
        CreateThreeDSecureToken = 'cyberSource/createThreeDSecureToken',
        TokenEndpoint = 'cyberSource/microform_tokenRequest'
    }

    export enum EDeviceDataCollectionEndpoints {
        staging = 'https://centinelapistag.cardinalcommerce.com/V1/Cruise/Collect',
        production = 'https://centinelapi.cardinalcommerce.com/V1/Cruise/Collect'
    }

    export enum EPaymentMethod {
        chcybersource = 'chcybersource'
    }

    export enum EMagentoCardTypes {
        '001' = 'VI',
        '002' = 'MC',
        '003' = 'AE',
        '004' = 'DI',
        '005' = 'DN',
        '007' = 'JCB',
        '042' = 'MI'
    }

    export enum EInputSelectors {
        number = '#barclaycard-microform-number-container',
        securityCode = '#barclaycard-microform-cvv-container'
    }

    export interface IFlexInstance {
        microform(config: IMicroformConfig): IMicroformInstance;
    }

    export interface IMicroformConfig {
        styles: IMicroformStylesConfig;
    }

    export interface IMicroformStylesConfig {
        input: {
            'font-size': string;
            'font-family': string;
            color: string;
        };
        '::placeholder': { color: string };
        ':disabled': { cursor: string };
        invalid: { color: string };
    }

    export interface IMicroformInstance {
        createField(name: string, options?: IMicroformFieldOptions): IMicroformField;
        createToken(
            options: IMicroformTokenOptions,
            callback: (error: { message: string; details?: { message: string }[] }, token: string) => void
        ): void;
    }

    export interface IMicroformTokenOptions {
        expirationMonth: string;
        expirationYear: string;
    }

    export interface IMicroformFieldOptions {
        placeholder: string;
        autoformat: boolean;
    }

    export interface IMicroformField {
        on(event: string, handler: (event: IMicroformChangeEvent | IMicroformAutofillEvent) => void): void;
        load(selector: string): void;
    }

    export interface IMicroformChangeEvent {
        card: ICard[];
        valid: boolean;
    }

    export interface IMicroformAutofillEvent {
        expirationMonth: string;
        expirationYear: string;
    }

    export interface ICard {
        cybsCardType: string;
    }

    export interface IPaymentMethod extends Checkout.IPaymentMethod {
        isTest: boolean;
        isVaultEnabled?: boolean;
    }

    export interface ISubmissionPaymentData extends Checkout.ISubmissionPaymentData {
        method: EPaymentMethod.chcybersource;
        additional_data: ISubmissionPaymentAdditionalData,
        ScreenHeight: number;
        ScreenWidth: number;
        TimeDifference: number;
        ColorDepth: number;
        JavaEnabled: boolean;
        JavaScriptEnabled: boolean;
        Language: string;
        AcceptContent: string;
    }

    export interface ISubmissionPaymentAdditionalData {
        cvv: string;
        ccType: string;
        expDate: string;
        store_cc?: boolean;
        token?: string;
        sessionId?: string;
    }

    export interface ICheckoutSubmitResponse {
        error?: string;
        code?: number;
        cca?: ICca;
        order?: IOrder;
    }

    export interface ICca {
        AcsUrl?: string;
        Payload?: string;
    }

    export interface IOrder {
        Consumer?: IOrderConsumer;
        OrderDetails?: IOrderDetails;
    }

    export interface IOrderDetails {
        Amount: number;
        CurrencyCode: string;
        OrderChannel: string;
        OrderNumber: number;
        TransactionId: string;
    }

    export interface IOrderConsumer {
        BillingAddress?: IOrderConsumerAddress;
        Email1?: string;
        ShippingAddress?: IOrderConsumerAddress;
    }

    export interface IOrderConsumerAddress {
        Address1: string;
        Address2: string;
        City: string;
        CountryCode: string;
        FirstName: string;
        LastName: string;
        Phone1: number;
        PostalCode: string;
        State: string;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Cardinal
    ////////
    ////////////////////////////////////////////

    export enum ECardinalEventName {
        setupComplete = 'payments.setupComplete',
        validated = 'payments.validated'
    }

    export interface ICardinalInstance {
        configure(options: ICardinalInstanceConfigureOptions): void;
        on(eventName: ECardinalEventName.setupComplete, setupCompleteHandler: (result: ICardinalSetupCompleteResult) => void): void;
        on(
            eventName: ECardinalEventName.validated,
            paymentValidatedHandler: (data: ICardinalPaymentValidatedData, jwt: string) => void
        ): void;
        off(eventName: ECardinalEventName): void;
        setup(eventName: 'init', options: ICardinalSetupOptions): void;
        binDetect(cardNumber: string): void;
        trigger(eventName: 'bin.process', cardNmber: string): Promise<any>;
        continue(eventName: 'cca', cca: ICca, order: IOrder): void;
    }

    export interface ISetupCardinalResponse {
        sessionId: string;
        token: string;
    }

    export interface ICardinalGetTokenResponse {
        accessToken: string;
        success: boolean;
        error_msg?: string;
    }

    export interface ICardinalSetupOptions {
        jwt: string;
    }

    export interface ICardinalSetupCompleteResult {
        sessionId: string;
    }

    export interface ICardinalPaymentValidatedData {
        ErrorNumber?: number;
        ErrorDescription?: string;
    }

    export interface ICardinalInstanceConfigureOptions {
        logging: {
            level: 'on';
        };
    }

    export interface ICardinalJwt {
        data?: {
            number?: string;
        };
    }
}
